import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import { useState } from 'react';
import type {
  EmptyCheckOutcome,
  RunStatus,
  StepFillSummary,
  StepRunReport,
} from './usePreviewTestRunner';

export interface PreviewTestingPanelProps {
  onFillStep: () => void;
  onRunToEnd: () => void;
  onStop: () => void;
  runStatus: RunStatus;
  lastFillResult: StepFillSummary | null;
  runReport: StepRunReport[] | null;
  activityLabel?: string | null;
}

const EMPTY_CHECK_LABEL: Record<EmptyCheckOutcome, string> = {
  'errors-fired': 'Leaving it empty shows errors correctly',
  'nothing-required': 'Nothing is required on this step',
  'skipped-already-filled': 'Empty check skipped — fields already had values',
  'no-error-despite-required':
    'Required fields let you continue while empty — worth a look',
};

const EMPTY_CHECK_CLASS_NAMES: Record<EmptyCheckOutcome, string> = {
  'errors-fired': 'text-text-success',
  'nothing-required': 'text-text-tertiary',
  'skipped-already-filled': 'text-text-tertiary',
  'no-error-despite-required': 'text-text-warning',
};

function fillSummaryLine(step: StepRunReport): string {
  const parts: string[] = [];
  if (step.filledCount > 0) {
    parts.push(
      `Filled ${step.filledCount} field${step.filledCount === 1 ? '' : 's'}`
    );
  } else if (step.alreadyFilledCount > 0) {
    parts.push('All fields already had values');
  } else {
    parts.push('No fields to fill');
  }
  if (step.skipped.length > 0) {
    parts.push(`${step.skipped.length} skipped`);
  }
  return parts.join(' · ');
}

function FillCoverageNote({ result }: { result: StepFillSummary }) {
  return (
    <div className="flex flex-col gap-xs">
      <span className="txt-small-medium text-text-primary">
        Pre-filled {result.filledCount} of {result.candidateCount} field
        {result.candidateCount === 1 ? '' : 's'}
        {result.alreadyFilledCount > 0
          ? ` (${result.alreadyFilledCount} already filled)`
          : ''}
      </span>
      {result.skipped.length > 0 && (
        <ul className="flex flex-col gap-xxs">
          {result.skipped.map((skip) => (
            <li
              key={skip.fieldId}
              className="txt-small-regular text-text-tertiary"
            >
              Skipped {skip.label}: {skip.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RunReportView({
  report,
  runStatus,
}: {
  report: StepRunReport[];
  runStatus: RunStatus;
}) {
  const failedStep = report.find((step) => step.haltReason);

  let headline: string;
  let headlineClassName: string;
  if (runStatus === 'done') {
    headline = `Test passed — went through all ${report.length} step${report.length === 1 ? '' : 's'}`;
    headlineClassName = 'text-text-success';
  } else if (failedStep) {
    headline = `Test stopped on "${failedStep.stepTitle}"`;
    headlineClassName = 'text-text-error';
  } else {
    headline = 'Test stopped';
    headlineClassName = 'text-text-primary';
  }

  return (
    <div className="flex flex-col gap-sm">
      <span className={`txt-small-medium ${headlineClassName}`}>
        {headline}
      </span>
      <ul className="flex flex-col gap-sm">
        {report.map((step) => (
          <li
            key={step.stepIndex}
            className="flex flex-col gap-xxs border-l-2 border-border-secondary pl-md"
          >
            <span className="txt-small-medium text-text-primary">
              {step.stepTitle}
            </span>
            <span
              className={`txt-small-regular ${EMPTY_CHECK_CLASS_NAMES[step.emptyCheck]}`}
            >
              {EMPTY_CHECK_LABEL[step.emptyCheck]}
            </span>
            <span className="txt-small-regular text-text-tertiary">
              {fillSummaryLine(step)}
            </span>
            {step.brokenRequiredRules.map((finding) => (
              <span
                key={finding}
                className="txt-small-regular text-text-warning"
              >
                Validation gap: {finding}
              </span>
            ))}
            {step.haltReason && (
              <span className="txt-small-regular text-text-error">
                Stopped here: {step.haltReason}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Floating preview-only control panel: fill the current step, or fill and run
 * the whole form end to end, and read back a coverage note / run report. Only
 * rendered when the renderer is in preview mode.
 */
export function PreviewTestingPanel({
  onFillStep,
  onRunToEnd,
  onStop,
  runStatus,
  lastFillResult,
  runReport,
  activityLabel,
}: PreviewTestingPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isRunning = runStatus === 'running';

  return (
    // Pinned to the viewport, so it knows nothing about a side panel sharing the right edge. The margin
    // moves it clear of whatever rail is open; pages with no rail leave the variable unset and it sits
    // exactly where it always did.
    <div
      className="fixed bottom-xl right-xl z-50 w-96 rounded-xl border border-border-secondary bg-bg-primary shadow-lg"
      style={{ marginRight: 'var(--right-rail-width, 0px)' }}
    >
      <button
        type="button"
        onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        className="flex w-full cursor-pointer items-center justify-between gap-md p-lg text-left"
        aria-expanded={!isCollapsed}
        aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
      >
        <span className="flex items-center gap-sm">
          <Icons.MagicWand01 className="size-4 text-text-secondary" />
          <span className="txt-small-medium text-text-primary">
            Preview testing
          </span>
        </span>
        {isCollapsed ? (
          <Icons.ChevronUp className="size-4 text-text-tertiary" />
        ) : (
          <Icons.ChevronDown className="size-4 text-text-tertiary" />
        )}
      </button>

      {!isCollapsed && (
        <div className="flex flex-col gap-xl px-lg pb-lg">
          <p className="txt-small-regular text-text-tertiary">
            Fill the step or whole form with test values.
          </p>
          <div className="flex items-center gap-sm">
            <Button
              variant="secondary"
              onClick={onFillStep}
              disabled={isRunning}
            >
              Fill this step
            </Button>
            {isRunning ? (
              <Button variant="secondary" onClick={onStop}>
                Stop
              </Button>
            ) : (
              <Button onClick={onRunToEnd}>Fill &amp; run to end</Button>
            )}
          </div>

          {isRunning && (
            <div className="flex items-start gap-md">
              <div className="flex size-4 shrink-0 items-center justify-center overflow-visible">
                <LoadingIndicator size={32} _className="scale-[0.375]" />
              </div>
              <span className="txt-small-regular text-text-tertiary">
                {activityLabel ?? 'Running...'}
              </span>
            </div>
          )}

          {((lastFillResult && !runReport) ||
            (runReport && runReport.length > 0)) && (
            <div className="max-h-80 overflow-y-auto rounded-lg bg-bg-light p-lg border">
              {lastFillResult && !runReport && (
                <FillCoverageNote result={lastFillResult} />
              )}
              {runReport && runReport.length > 0 && (
                <RunReportView report={runReport} runStatus={runStatus} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
